/**
 * Materials Marketplace
 * Professional construction materials marketplace with inventory and bulk pricing
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  ShoppingCart,
  Add,
  Remove,
  LocalShipping,
  Inventory,
  Calculate,
  RequestQuote,
  CompareArrows,
  Favorite,
  Info,
  Verified,
  Schedule,
  LocationOn
} from '@mui/icons-material';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { formatCurrency } from '../../utils/helpers';
import DialogCloseButton from '../../components/common/DialogCloseButton';

// Mock data for materials
const mockMaterialsData = [
  {
    id: 1,
    name: 'Premium Portland Cement',
    description: 'High-quality Portland cement for all construction needs. Meets SABS standards.',
    price: 12.50,
    unit: 'bag',
    unitWeight: '50kg',
    minOrder: 20,
    maxOrder: 1000,
    inStock: 2500,
    bulkPricing: [
      { quantity: 20, price: 12.50 },
      { quantity: 100, price: 11.75 },
      { quantity: 500, price: 10.90 },
      { quantity: 1000, price: 10.25 }
    ],
    images: ['/api/placeholder/300/300'],
    vendor: {
      id: 1,
      name: 'Zimbabwe Cement Company',
      logo: '/api/placeholder/50/50',
      rating: 4.8,
      reviewCount: 324,
      isVerified: true,
      location: 'Harare'
    },
    category: 'Cement',
    specifications: {
      'Compressive Strength': '42.5 MPa',
      'Setting Time': '45-60 minutes',
      'Fineness': '350 m²/kg',
      'Standards': 'SABS 50197'
    },
    features: ['Premium Grade', 'SABS Certified', 'Fast Setting'],
    deliveryTime: '2-3 days',
    freeDelivery: true,
    freeDeliveryMin: 500,
    rating: 4.8,
    reviewCount: 89,
    isPopular: true,
    discount: 8
  },
  {
    id: 2,
    name: 'High Tensile Steel Bars (12mm)',
    description: 'Grade 500 high tensile deformed steel reinforcement bars for concrete structures.',
    price: 850,
    unit: 'tonne',
    unitWeight: '1000kg',
    minOrder: 1,
    maxOrder: 50,
    inStock: 125,
    bulkPricing: [
      { quantity: 1, price: 850 },
      { quantity: 5, price: 820 },
      { quantity: 10, price: 795 },
      { quantity: 25, price: 775 }
    ],
    images: ['/api/placeholder/300/300'],
    vendor: {
      id: 2,
      name: 'Steelworks Zimbabwe',
      logo: '/api/placeholder/50/50',
      rating: 4.6,
      reviewCount: 156,
      isVerified: true,
      location: 'Bulawayo'
    },
    category: 'Steel',
    specifications: {
      'Grade': 'Grade 500',
      'Diameter': '12mm',
      'Length': '12 meters',
      'Standards': 'SABS 920'
    },
    features: ['High Tensile', 'Corrosion Resistant', 'SABS Certified'],
    deliveryTime: '3-5 days',
    freeDelivery: false,
    deliveryCost: 50,
    rating: 4.6,
    reviewCount: 42,
    isNew: true
  },
  {
    id: 3,
    name: 'Clay Facing Bricks',
    description: 'Premium clay facing bricks with excellent thermal properties and durability.',
    price: 0.85,
    unit: 'piece',
    unitWeight: '3.2kg',
    minOrder: 1000,
    maxOrder: 50000,
    inStock: 75000,
    bulkPricing: [
      { quantity: 1000, price: 0.85 },
      { quantity: 5000, price: 0.80 },
      { quantity: 10000, price: 0.75 },
      { quantity: 25000, price: 0.70 }
    ],
    images: ['/api/placeholder/300/300'],
    vendor: {
      id: 3,
      name: 'Premier Brick Works',
      logo: '/api/placeholder/50/50',
      rating: 4.9,
      reviewCount: 278,
      isVerified: true,
      location: 'Gweru'
    },
    category: 'Bricks',
    specifications: {
      'Dimensions': '222 x 106 x 73mm',
      'Compressive Strength': '25 MPa',
      'Water Absorption': '< 15%',
      'Thermal Conductivity': '0.6 W/mK'
    },
    features: ['Premium Quality', 'Weather Resistant', 'Standard Size'],
    deliveryTime: '5-7 days',
    freeDelivery: true,
    freeDeliveryMin: 5000,
    rating: 4.9,
    reviewCount: 167,
    isFeatured: true
  },
  {
    id: 4,
    name: 'Galvanized Corrugated Iron Sheets',
    description: '0.4mm thick galvanized corrugated iron sheets for roofing applications.',
    price: 18.50,
    unit: 'sheet',
    unitWeight: '12kg',
    minOrder: 10,
    maxOrder: 500,
    inStock: 890,
    bulkPricing: [
      { quantity: 10, price: 18.50 },
      { quantity: 50, price: 17.80 },
      { quantity: 100, price: 17.20 },
      { quantity: 250, price: 16.50 }
    ],
    images: ['/api/placeholder/300/300'],
    vendor: {
      id: 4,
      name: 'Roofing Solutions Ltd',
      logo: '/api/placeholder/50/50',
      rating: 4.7,
      reviewCount: 203,
      isVerified: true,
      location: 'Harare'
    },
    category: 'Roofing',
    specifications: {
      'Thickness': '0.4mm',
      'Length': '3 meters',
      'Width': '762mm',
      'Coating': 'Z275 Galvanized'
    },
    features: ['Galvanized', 'Corrosion Resistant', '20 Year Warranty'],
    deliveryTime: '1-2 days',
    freeDelivery: true,
    freeDeliveryMin: 100,
    rating: 4.7,
    reviewCount: 93,
    availability: 'limited'
  }
];

const MaterialsMarketplace = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [bulkCalculatorOpen, setBulkCalculatorOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [calculatorQuantity, setCalculatorQuantity] = useState(1);
  const [quoteRequestOpen, setQuoteRequestOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('relevance');

  // Load materials data
  useEffect(() => {
    const loadMaterials = async () => {
      setLoading(true);
      // TODO: Replace with real API call to /api/marketplace/materials
      setTimeout(() => {
        setMaterials([]); // Empty - no mock data
        setLoading(false);
      }, 500);
    };

    loadMaterials();
  }, [filters, sortBy]);

  // Cart management
  const addToCart = (material, quantity = material.minOrder) => {
    setCart(prev => {
      const existingItem = prev.find(item => item.id === material.id);
      if (existingItem) {
        return prev.map(item =>
          item.id === material.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prev, { ...material, quantity, addedAt: new Date() }];
      }
    });
  };

  const updateCartQuantity = (materialId, quantity) => {
    setCart(prev =>
      prev.map(item =>
        item.id === materialId
          ? { ...item, quantity: Math.max(quantity, item.minOrder) }
          : item
      )
    );
  };

  const removeFromCart = (materialId) => {
    setCart(prev => prev.filter(item => item.id !== materialId));
  };

  // Get bulk pricing for quantity
  const getBulkPrice = (material, quantity) => {
    const bulkTiers = material.bulkPricing.sort((a, b) => b.quantity - a.quantity);
    const applicableTier = bulkTiers.find(tier => quantity >= tier.quantity);
    return applicableTier ? applicableTier.price : material.price;
  };

  // Calculate total for material with quantity
  const calculateTotal = (material, quantity) => {
    const unitPrice = getBulkPrice(material, quantity);
    return unitPrice * quantity;
  };

  // Get next bulk tier discount
  const getNextBulkTier = (material, quantity) => {
    const currentPrice = getBulkPrice(material, quantity);
    const nextTier = material.bulkPricing.find(tier => 
      tier.quantity > quantity && tier.price < currentPrice
    );
    return nextTier;
  };

  // Handle search
  const handleSearch = (searchTerm) => {
    // Implement search logic
    console.log('Searching for:', searchTerm);
  };

  // Handle filters
  const handleFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle sort
  const handleSort = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Bulk Calculator Component
  const BulkCalculator = () => (
    <Dialog
      open={bulkCalculatorOpen}
      onClose={() => setBulkCalculatorOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogCloseButton onClick={() => setBulkCalculatorOpen(false)} />
      <DialogTitle>
        Bulk Pricing Calculator - {selectedMaterial?.name}
      </DialogTitle>
      <DialogContent>
        {selectedMaterial && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={calculatorQuantity}
                  onChange={(e) => setCalculatorQuantity(Number(e.target.value))}
                  inputProps={{ min: selectedMaterial.minOrder, max: selectedMaterial.maxOrder }}
                  helperText={`Min: ${selectedMaterial.minOrder}, Max: ${selectedMaterial.maxOrder}`}
                />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6">Pricing Breakdown</Typography>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Unit Price</TableCell>
                          <TableCell>Savings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedMaterial.bulkPricing.map((tier, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              bgcolor: calculatorQuantity >= tier.quantity ? 'action.selected' : 'inherit'
                            }}
                          >
                            <TableCell>{tier.quantity}+ {selectedMaterial.unit}s</TableCell>
                            <TableCell>{formatCurrency(tier.price)}</TableCell>
                            <TableCell>
                              {index === 0 ? '-' : 
                                `${(((selectedMaterial.bulkPricing[0].price - tier.price) / selectedMaterial.bulkPricing[0].price) * 100).toFixed(1)}%`
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Order Summary
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Quantity:</Typography>
                        <Typography fontWeight={600}>
                          {calculatorQuantity} {selectedMaterial.unit}s
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Unit Price:</Typography>
                        <Typography fontWeight={600}>
                          {formatCurrency(getBulkPrice(selectedMaterial, calculatorQuantity))}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Total:</Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(calculateTotal(selectedMaterial, calculatorQuantity))}
                        </Typography>
                      </Box>

                      {getNextBulkTier(selectedMaterial, calculatorQuantity) && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            Order {getNextBulkTier(selectedMaterial, calculatorQuantity).quantity} {selectedMaterial.unit}s 
                            to get {formatCurrency(getNextBulkTier(selectedMaterial, calculatorQuantity).price)} per {selectedMaterial.unit}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Delivery Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Schedule sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      Delivery: {selectedMaterial.deliveryTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping sx={{ fontSize: 16, color: selectedMaterial.freeDelivery ? 'success.main' : 'inherit' }} />
                    <Typography variant="body2" color={selectedMaterial.freeDelivery ? 'success.main' : 'inherit'}>
                      {selectedMaterial.freeDelivery 
                        ? `Free delivery (min order: ${selectedMaterial.freeDeliveryMin} ${selectedMaterial.unit}s)`
                        : `Delivery: ${formatCurrency(selectedMaterial.deliveryCost || 50)}`
                      }
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBulkCalculatorOpen(false)}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (selectedMaterial) {
              addToCart(selectedMaterial, calculatorQuantity);
              setBulkCalculatorOpen(false);
            }
          }}
          disabled={calculatorQuantity < selectedMaterial?.minOrder}
        >
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Enhanced Product Card for Materials
  const MaterialCard = ({ material }) => (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      {/* Image and badges */}
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={material.images[0]}
          alt={material.name}
          sx={{ width: '100%', height: 200, objectFit: 'cover' }}
        />
        
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {material.isPopular && <Chip label="Popular" size="small" color="warning" />}
          {material.isNew && <Chip label="New" size="small" color="success" />}
          {material.isFeatured && <Chip label="Featured" size="small" color="primary" />}
          {material.discount && <Chip label={`${material.discount}% OFF`} size="small" color="error" />}
        </Box>

        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}>
            <Favorite />
          </IconButton>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Vendor info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar src={material.vendor.logo} sx={{ width: 20, height: 20, mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {material.vendor.name}
          </Typography>
          {material.vendor.isVerified && <Verified color="primary" sx={{ ml: 0.5, fontSize: 12 }} />}
        </Box>

        {/* Product name */}
        <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
          {material.name}
        </Typography>

        {/* Price and unit */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
            {formatCurrency(material.price)}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              per {material.unit}
            </Typography>
          </Typography>
          
          {material.bulkPricing.length > 1 && (
            <Typography variant="caption" color="success.main">
              Bulk discounts available from {formatCurrency(material.bulkPricing[material.bulkPricing.length - 1].price)}
            </Typography>
          )}
        </Box>

        {/* Key specs */}
        <Box sx={{ mb: 2 }}>
          <Chip label={`Min: ${material.minOrder} ${material.unit}s`} size="small" variant="outlined" sx={{ mr: 1 }} />
          <Chip 
            label={`${material.inStock} in stock`} 
            size="small" 
            color={material.inStock < 100 ? 'warning' : 'success'}
            variant="outlined"
          />
        </Box>

        {/* Features */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {material.features.slice(0, 2).map((feature, index) => (
            <Chip key={index} label={feature} size="small" />
          ))}
        </Box>

        {/* Delivery info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocalShipping sx={{ fontSize: 16, color: material.freeDelivery ? 'success.main' : 'inherit' }} />
          <Typography variant="caption" color={material.freeDelivery ? 'success.main' : 'text.secondary'}>
            {material.freeDelivery ? 'Free delivery' : 'Paid delivery'} • {material.deliveryTime}
          </Typography>
        </Box>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Calculate />}
              onClick={() => {
                setSelectedMaterial(material);
                setCalculatorQuantity(material.minOrder);
                setBulkCalculatorOpen(true);
              }}
              size="small"
            >
              Calculate
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={() => addToCart(material)}
              size="small"
            >
              Add to Cart
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );

  return (
    <>
      <MarketplaceLayout
        category="materials"
        title="Construction Materials"
        description="Premium construction materials from verified suppliers across Zimbabwe. Bulk pricing, quality guaranteed, fast delivery."
        data={materials}
        loading={loading}
        onSearch={handleSearch}
        onFilter={handleFilters}
        onSort={handleSort}
        totalResults={materials.length}
        customActions={[
          {
            icon: <Badge badgeContent={cart.length} color="primary"><ShoppingCart /></Badge>,
            name: 'Shopping Cart',
            action: () => console.log('Open cart')
          },
          {
            icon: <RequestQuote />,
            name: 'Request Quote',
            action: () => setQuoteRequestOpen(true)
          }
        ]}
      >
        {/* Materials Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                <Skeleton variant="rectangular" height={400} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {materials.map((material) => (
              <Grid item key={material.id} xs={12} sm={6} md={4} lg={3}>
                <MaterialCard material={material} />
              </Grid>
            ))}
          </Grid>
        )}
      </MarketplaceLayout>

      {/* Bulk Calculator Dialog */}
      <BulkCalculator />

      {/* Shopping Cart Summary (if items in cart) */}
      {cart.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'primary.main',
            color: 'white',
            px: 3,
            py: 2,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <ShoppingCart />
          <Typography>
            {cart.reduce((sum, item) => sum + item.quantity, 0)} items • {' '}
            {formatCurrency(cart.reduce((sum, item) => sum + calculateTotal(item, item.quantity), 0))}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => console.log('View cart')}
          >
            View Cart
          </Button>
        </Box>
      )}
    </>
  );
};

export default MaterialsMarketplace;